import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT_INFO, IMAGES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", institute: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Message sent! We'll be in touch soon.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const details = [
    { icon: Phone, label: "Phone", value: CONTACT_INFO.phone },
    { icon: Mail, label: "Email", value: CONTACT_INFO.email },
    { icon: MapPin, label: "Address", value: CONTACT_INFO.address },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Contact"
        titleLines={["Let's talk about", "your institute."]}
        subtitle="Have a question or want a walkthrough? Send us a message and our team will get back to you shortly."
      />

      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr]">
          {/* Left: details + map */}
          <Reveal className="space-y-6">
            <div className="space-y-4">
              {details.map((d) => (
                <div key={d.label} className="flex items-start gap-4 rounded-xl border border-zinc-800 bg-surface p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-zinc-700 bg-black/40 text-brand">
                    <d.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{d.label}</p>
                    <p className="mt-1 text-sm font-medium text-white">{d.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative overflow-hidden rounded-xl border border-zinc-800" data-testid="contact-map">
              <img src={IMAGES.map} alt="Location map placeholder" className="aspect-[16/10] w-full object-cover opacity-70" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/60 px-4 py-2 text-sm text-white backdrop-blur">
                  <MapPin className="h-4 w-4 text-brand" /> Google Maps placeholder
                </span>
              </div>
            </div>
          </Reveal>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-zinc-800 bg-surface p-8 lg:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-zinc-300">Name *</Label>
                  <Input id="name" data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Your name" className="border-zinc-700 bg-black/40 text-white placeholder:text-zinc-600 focus-visible:ring-brand" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-300">Email *</Label>
                  <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={update("email")} placeholder="you@institute.com" className="border-zinc-700 bg-black/40 text-white placeholder:text-zinc-600 focus-visible:ring-brand" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="institute" className="text-zinc-300">Institute</Label>
                  <Input id="institute" data-testid="contact-institute" value={form.institute} onChange={update("institute")} placeholder="Institute name" className="border-zinc-700 bg-black/40 text-white placeholder:text-zinc-600 focus-visible:ring-brand" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-zinc-300">Phone</Label>
                  <Input id="phone" data-testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder="+1 555 000 0000" className="border-zinc-700 bg-black/40 text-white placeholder:text-zinc-600 focus-visible:ring-brand" />
                </div>
              </div>
              <div className="mt-5 space-y-2">
                <Label htmlFor="message" className="text-zinc-300">Message *</Label>
                <Textarea id="message" data-testid="contact-message" value={form.message} onChange={update("message")} placeholder="Tell us about your institute and what you're looking for..." rows={5} className="resize-none border-zinc-700 bg-black/40 text-white placeholder:text-zinc-600 focus-visible:ring-brand" />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover disabled:opacity-60"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <>Send Message <Send className="h-4 w-4" /></>}
              </button>
              <p className="mt-4 text-center text-xs text-zinc-500">We'll never share your details. Fields marked * are required.</p>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
